import React, { useEffect, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import { Button } from "@mui/material";
import { useSalesInvoiceContext } from "../../../context/SalesInvoiceContext";
import { formatDate } from "../../../utils/dateFormatter";
import "./salesInvoices.css";

const SalesInvoices = () => {
  const { invoices, loading, getSalesInvoices, cancelSalesInvoice } =
    useSalesInvoiceContext();

  useEffect(() => {
    getSalesInvoices();
  }, []);

  // ✅ فواتير البيع النشطة فقط — الملغية (المرتجعة) لها صفحتها الخاصة
  const activeInvoices = useMemo(
    () => invoices.filter((inv) => inv.is_canceled != 1),
    [invoices]
  );

  const handleCancelInvoice = async (id) => {
    if (
      !window.confirm(
        "هل أنت متأكد من إلغاء هذه الفاتورة؟\nسيتم إرجاع الكميات وتحديث الرصيد."
      )
    ) {
      return;
    }

    try {
      const response = await cancelSalesInvoice(id);
      if (response.status === "success") {
        alert("✅ " + response.message);
        getSalesInvoices(); // إعادة تحميل الفواتير
      }
    } catch (err) {
      alert("❌ " + (err.response?.data?.message || "حدث خطأ أثناء الإلغاء"));
    }
  };

  const columns = [
    {
      accessorKey: "id",
      header: "رقم الفاتورة",
      size: 100,
      Cell: ({ row }) => `#${row.original.id}`,
    },
    {
      accessorKey: "party_name",
      header: "اسم الطرف",
      size: 200,
      Cell: ({ row }) => (
        <a
          href={`/${row.original.party_type}/${
            row.original.party_type === "supplier"
              ? row.original.supplier_id
              : row.original.customer_id
          }`}
          style={{
            color: "#3b82f6",
            textDecoration: "none",
            fontWeight: 500,
          }}
          onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
          onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
        >
          {row.original.party_name}
        </a>
      ),
    },
    {
      accessorKey: "party_phone",
      header: "هاتف الطرف",
      size: 150,
    },
    {
      accessorKey: "total_amount",
      header: "المبلغ الإجمالي",
      size: 150,
      Cell: ({ cell }) => `${parseFloat(cell.getValue()).toFixed(2)} ₪`,
    },
    {
      accessorKey: "date",
      header: "تاريخ الفاتورة",
      size: 150,
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
    {
      accessorKey: "notes",
      header: "ملاحظات",
      size: 250,
      Cell: ({ cell }) => cell.getValue() || "-",
    },
    {
      accessorKey: "is_canceled",
      header: "الحالة",
      size: 100,
      Cell: ({ row }) =>
        row.original.is_canceled == 1 ? (
          <span
            style={{
              padding: "4px 12px",
              backgroundColor: "#fee2e2",
              color: "#dc2626",
              borderRadius: "4px",
              fontWeight: "600",
              fontSize: "13px",
            }}
          >
            ملغية
          </span>
        ) : (
          <span
            style={{
              padding: "4px 12px",
              backgroundColor: "#d1fae5",
              color: "#059669",
              borderRadius: "4px",
              fontWeight: "600",
              fontSize: "13px",
            }}
          >
            نشطة
          </span>
        ),
    },
    {
      header: "الإجراءات",
      size: 200,
      Cell: ({ row }) => (
        <div style={{ display: "flex", gap: 8 }}>
          <a
            href={`/sales-invoice/${row.original.id}`}
            style={{
              padding: "6px 16px",
              backgroundColor: "#10b981",
              color: "white",
              textDecoration: "none",
              borderRadius: "4px",
              fontSize: "0.875rem",
              fontWeight: 500,
              display: "inline-block",
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#059669")}
            onMouseLeave={(e) => (e.target.style.backgroundColor = "#10b981")}
          >
            عرض التفاصيل
          </a>
          {row.original.is_canceled != 1 && (
            <Button
              variant="contained"
              size="small"
              sx={{
                backgroundColor: "#ef4444",
                "&:hover": { backgroundColor: "#dc2626" },
              }}
              onClick={() => handleCancelInvoice(row.original.id)}
            >
              إلغاء
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="sales-invoices-page">
      <div className="page-container">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h2 className="page-title" style={{ margin: 0 }}>
            فواتير البيع ({activeInvoices.length})
          </h2>
          <a
            href="/cart"
            style={{
              backgroundColor: "#10b981",
              color: "white",
              padding: "10px 20px",
              borderRadius: "4px",
              fontWeight: 600,
              textDecoration: "none",
              display: "inline-block",
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#059669")}
            onMouseLeave={(e) => (e.target.style.backgroundColor = "#10b981")}
          >
            + إضافة فاتورة بيع جديدة
          </a>
        </div>

        {loading ? (
          <p className="loading-text">جارٍ تحميل البيانات...</p>
        ) : (
          <div className="table-wrapper">
            <MaterialReactTable
              columns={columns}
              data={activeInvoices}
              enableColumnOrdering
              localization={{
                toolbar: { searchPlaceholder: "بحث..." },
                noRecordsToDisplay: "لا توجد فواتير",
              }}
              muiTableBodyRowProps={{
                sx: {
                  "&:not(:last-child)": {
                    borderBottom: "8px solid transparent",
                  },
                  "& td": { borderBottom: "1px solid #ddd" },
                },
              }}
              muiTableBodyCellProps={{
                sx: { textAlign: "right", padding: "12px 16px" },
              }}
              muiTableHeadCellProps={{
                sx: {
                  textAlign: "right",
                  padding: "12px 16px",
                  backgroundColor: "#f5f5f5",
                  fontWeight: 600,
                },
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesInvoices;
